import { useForm, type UseFormReturn } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject, useToast } from '@kanda-libs/ks-design-library';
import {
  actions,
  Document,
  InfoWorkType,
  JobItem,
  WorkType,
} from '@kanda-libs/ks-frontend-services';
import { URLS } from 'config';
import { useApiError, useAppDispatch } from 'hooks';
import { useEnquiryContext } from 'pages/Enquiry/context';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { getEnquiryTotalFromItems } from 'utils';
import { getDefaultValues } from './helpers';

export interface JobInfoValues {
  work_type: WorkType;
  job_items: JobItem[];
}

export interface EnquiryQuoteConfirmDetailsFormValues {
  job_info: JobInfoValues;
}

export interface EnquiryQuoteConfirmDetailsFormHook {
  form: UseFormReturn<EnquiryQuoteConfirmDetailsFormValues>;
  onSubmit: (
    formValues: StringIndexedObject<EnquiryQuoteConfirmDetailsFormValues>,
  ) => void;
  isSubmitting: boolean;
  isLoading: boolean;
  quote: Document | undefined;
  total: number;
  validExtractedWorkType: boolean;
  infoWorkTypes: InfoWorkType[] | undefined;
  workType: WorkType | undefined;
}

export default function useEnquiryQuoteConfirmDetailsForm(): EnquiryQuoteConfirmDetailsFormHook {
  const { id, enquiry, workTypes, infoWorkTypes } = useEnquiryContext();
  const { push } = useHistory();
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const onError = useApiError(
    'Unknown error confirming details - please try again ro contact Ideal4Finance',
  );
  const onSubmitError = useApiError(
    'Unknown error submitting equiry - please try again ro contact Ideal4Finance',
  );
  const { showSuccess } = useToast();

  const extractedWorkType = useMemo(
    () => enquiry?.enquiry_info?.job_info?.work_type,
    [enquiry],
  );

  const extractedJobItems = useMemo(
    () => enquiry?.enquiry_info?.job_info?.job_items,
    [enquiry],
  );

  const quote = useMemo(() => enquiry?.enquiry_info?.quote, [enquiry]);

  const validExtractedWorkType = useMemo(
    () =>
      Boolean(
        workTypes && extractedWorkType && workTypes.includes(extractedWorkType),
      ),
    [workTypes, extractedWorkType],
  );

  const form = useForm<EnquiryQuoteConfirmDetailsFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<EnquiryQuoteConfirmDetailsFormValues>) => {
      if (!id) return;
      const fv = formValues as unknown as EnquiryQuoteConfirmDetailsFormValues;
      setIsSubmitting(true);
      dispatch(
        actions.postEnquiryJobInfo({
          params: { id },
          body: fv.job_info,
          onError: (err) => {
            setIsSubmitting(false);
            onError(err);
          },
          onSuccess: () => {
            dispatch(
              actions.postEnquirySubmit({
                params: { id },
                onError: (err) => {
                  setIsSubmitting(false);
                  onSubmitError(err);
                },
                onSuccess: () => {
                  setIsSubmitting(false);
                  showSuccess('Details sent for confirmation');
                  push(generatePath(URLS.enquiryQuoteComplete, { id }));
                },
              }),
            );
          },
        }),
      );
    },
    [dispatch, id, onError, onSubmitError, showSuccess, push],
  );

  const workType = form.watch('job_info.work_type');

  const isLoading = useMemo(() => !enquiry, [enquiry]);

  const jobItems = form.watch('job_info.job_items');

  const total = useMemo(() => {
    if (!jobItems || jobItems.length === 0) return 0;
    return getEnquiryTotalFromItems(jobItems);
  }, [jobItems]);

  useEffect(() => {
    if (!enquiry || !id || !workTypes) return;
    if (!quote) {
      push(generatePath(URLS.enquiryQuoteUploadQuote, { id }));
      return;
    }
    const defaultValues = getDefaultValues(
      workTypes,
      extractedWorkType,
      extractedJobItems,
    );
    form.reset({ job_info: defaultValues });
  }, [
    id,
    enquiry,
    quote,
    workTypes,
    extractedWorkType,
    extractedJobItems,
    form,
    push,
  ]);

  return {
    form,
    onSubmit,
    isSubmitting,
    isLoading,
    quote,
    total,
    workType,
    infoWorkTypes,
    validExtractedWorkType,
  };
}
